import { round } from 'lodash';
import { AccordionWidgetData, RiskOverviewMetrics } from '../../../models/RiskProducts';
import {
  AGENCY_COMPOSITE_RATING_TITLE,
  COUNTRY_RISK_SCORE_TITLE,
  CQD,
  CQS_OUTLOOK,
  CREDIT_QUALITY_SCORE_TITLE,
  CREDIT_RISK_ESTIMATE_TITLE,
  CRL,
  FALLEN_ANGEL_SCORE_TITLE,
  // PREVIOUS,
  Y3_PERIOD,
  Y5_PERIOD,
  FS,
} from './RiskOverviewWidget.constants';

export const formatCreValue = (creValue: number | null): string | null => {
  if (creValue) {
    return `${round(creValue * 100, 2)}%`;
  }
  return null;
};

export const getRiskOverviewData = (metrics: RiskOverviewMetrics | undefined): AccordionWidgetData[] => {
  if (!metrics) {
    return [];
  }

  return [
    {
      item: {
        label: CREDIT_QUALITY_SCORE_TITLE,
        value: metrics.cqs?.cqs,
        canAccess: metrics.cqs?.canAccess,
        tooltip: 'Medium-term credit score for corporate and financial issuers.',
      },
      children: [
        {
          label: CQS_OUTLOOK,
          value: metrics.cqs.cqs_outlook,
          canAccess: metrics.cqs?.canAccess,
          tooltip:
            'Near-term (3 month) outlook for the CQS based on its trend, volatility and distance from the upgrade/downgrade border.',
        },
        {
          label: CQD,
          value: metrics.cqs?.credit_quality_difference,
          canAccess: metrics.cqs?.canAccess,
          tooltip: "Difference between company's CQS and its ACR Score.",
        },
        {
          label: FS,
          value: metrics.fscore?.fscore && round(metrics.fscore?.fscore),
          canAccess: metrics.cqs?.canAccess,
          tooltip: "Credit Quality Score based solely on an issuer's financial statement information",
        },
        {
          label: COUNTRY_RISK_SCORE_TITLE,
          value: metrics.cqs?.country_risk_score,
          canAccess: metrics.cqs?.canAccess,
          tooltip: 'An operational risk index on a scale of 0-100, with 100 being the highest risk.',
          infoContent:
            '<p>Fitch Solutions BMI’s Operational Risk Index quantitatively compares the challenges of operating in 200+ countries worldwide. The index scores each country on a scale of 0-100, with 100 being the highest risk.</p><p>To learn more about <a href="https://www.fitchsolutions.com/products/country-risk" target="_blank" rel="noreferrer">BMI Country Risk</a> and <a href="https://www.fitchsolutions.com/sites/default/files/2023-04/BMI_Country_Risk_Index_Overview.pdf" target="_blank" rel="noreferrer">Index Methodology</a></p>',
        },
      ],
    },
    {
      item: {
        label: FALLEN_ANGEL_SCORE_TITLE,
        value: metrics.fas?.fallen_angel_score,
        canAccess: metrics.fas?.canAccess,
        tooltip: 'More on FAS',
        infoContent: `<p>The Fallen Angel Score (FAS) represents the likelihood of a company with a BBB ACR being downgraded to high yield within the next 12 months.<br>
        Note that this measure is only calculated for BBB ACR companies, and will be blank for companies with any other ACR.</p>`,
      },
      children: [],
    },
    {
      item: {
        label: AGENCY_COMPOSITE_RATING_TITLE,
        value: metrics.acr?.acr,
        canAccess: metrics.acr?.canAccess,
        tooltip: 'The average rating from one or more rating agencies.',
      },
      children: [
        // {
        //   label: PREVIOUS,
        //   value: metrics.acr?.acr_previous,
        //   canAccess: metrics.acr?.canAccess,
        //   tooltip: 'Previous Agency Composite Rating.',
        // },
      ],
    },
    {
      item: {
        label: CREDIT_RISK_ESTIMATE_TITLE,
        value: formatCreValue(metrics.cre?.cre_1y),
        canAccess: metrics.cre?.canAccess,
        tooltip: '12-Month Forward Probability of Default.',
      },
      children: [
        {
          label: Y3_PERIOD,
          value: formatCreValue(metrics.cre?.cre_3y),
          canAccess: metrics.cre?.canAccess,
          tooltip: '3-Yr cumulative default probability based on equity market and fundamental information.',
        },
        {
          label: Y5_PERIOD,
          value: formatCreValue(metrics.cre?.cre_5y),
          canAccess: metrics.cre?.canAccess,
          tooltip: '5-Yr cumulative default probability based on equity market and fundamental information.',
        },
        {
          label: CRL,
          value: metrics.cqs?.credit_risk_level,
          canAccess: metrics.cre?.canAccess,
          tooltip: "Level of a company's CRE relative to other companies within the same CRE model.",
        },
      ],
    },
  ];
};
